import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useApiMutation } from '../useApiMutation';
import { invoiceKeys, jobKeys, progressPaymentKeys } from '@/lib/queryKeys';
import { invoiceEndpoints } from '@/lib/api/endpoints';
import { apiPost, operationsClient } from '@/lib/api/client';
import type { Invoice, CreateInvoiceDto } from '@/types/invoice';
import { normalizeInvoice } from './useInvoice';

export function useCreateInvoice() {
  const queryClient = useQueryClient();

  return useApiMutation<Invoice, CreateInvoiceDto>({
    mutationFn: ({ jobId, ...dto }) => {
      if (!jobId) {
        throw new Error('A job is required to create an invoice.');
      }

      return apiPost<Record<string, unknown>, Omit<CreateInvoiceDto, 'jobId'>>(
        operationsClient,
        invoiceEndpoints.create(jobId),
        dto,
      ).then(normalizeInvoice);
    },
    onSuccess: (invoice, variables) => {
      void queryClient.invalidateQueries({ queryKey: invoiceKeys.all() });
      if (invoice.jobId) {
        void queryClient.invalidateQueries({ queryKey: jobKeys.detail(invoice.jobId) });
        void queryClient.invalidateQueries({ queryKey: jobKeys.lists() });
        void queryClient.invalidateQueries({ queryKey: invoiceKeys.byJob(invoice.jobId) });
        void queryClient.invalidateQueries({ queryKey: jobKeys.financialSummary(invoice.jobId) });
        if (variables.progressPaymentId) {
          void queryClient.invalidateQueries({
            queryKey: progressPaymentKeys.byJob(invoice.jobId),
          });
        }
      }
      toast.success('Invoice created');
    },
  });
}
